
import { useAdminInterface } from '@/hooks/useAdminInterface';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { removeVietnameseAccents } from '@/components/admin/table/tableUtils';

/**
 * Hook for managing UI state in the admin page with enhanced mobile support and elastic search
 */
export const useAdminPageUI = () => {
  const {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    isSearchVisible,
    setIsSearchVisible,
    toggleSearchVisibility,
    clearSearch,
    lastSearch
  } = useAdminInterface();
  
  // Remember previous search history
  const [recentSearches, setRecentSearches] = useState<string[]>(lastSearch || []);
  
  // Track if search is currently active
  const [isSearchActive, setIsSearchActive] = useState(false);
  
  // Debounce timer for search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // For elastic search results
  const [elasticResults, setElasticResults] = useState<Attendee[]>([]);
  
  // Clear the debounce timer when component unmounts
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Elastic search function that filters attendees immediately
  const elasticSearch = useCallback((query: string, attendeesList: Attendee[]) => {
    if (!query.trim() || !attendeesList.length) {
      setElasticResults([]);
      return [];
    }
    
    // Normalize query for accent-insensitive search
    const normalizedQuery = removeVietnameseAccents(query.toLowerCase().trim());
    
    // Filter attendees
    const results = attendeesList.filter(attendee => {
      const name = removeVietnameseAccents((attendee.name || "").toLowerCase());
      const email = removeVietnameseAccents((attendee.email || "").toLowerCase());
      const phone = removeVietnameseAccents((attendee.phone || "").toLowerCase());
      const code = removeVietnameseAccents((attendee.code || "").toLowerCase());
      const classValue = removeVietnameseAccents((attendee.class || "").toLowerCase());
      
      return name.includes(normalizedQuery) || 
             email.includes(normalizedQuery) || 
             phone.includes(normalizedQuery) || 
             classValue.includes(normalizedQuery) ||
             code.includes(normalizedQuery);
    });
    
    setElasticResults(results);
    return results;
  }, []);
  
  // Enhanced search function with elastic search support
  const performSearch = useCallback((query: string, attendeesList: Attendee[] = [], callback?: () => void) => {
    // Update the main search query
    setSearchQuery(query);
    
    // Set search as active
    setIsSearchActive(!!query.trim());
    
    // Clear any existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Immediate elastic search for fast feedback
    if (attendeesList.length > 0) {
      elasticSearch(query, attendeesList);
    }
    
    // Debounce saving to search history for 800ms
    searchDebounceRef.current = setTimeout(() => {
      // Save to search history if query is long enough and not already in history
      if (query.trim().length >= 2 && !recentSearches.includes(query.trim())) {
        setRecentSearches(prev => {
          const updated = [query.trim(), ...prev.slice(0, 4)];
          const uniqueSearches = [...new Set(updated)]; // Remove duplicates
          
          // Save to localStorage for persistence
          try {
            localStorage.setItem('admin_last_searches', JSON.stringify(uniqueSearches));
          } catch (e) {
            console.error('Failed to save search history to localStorage', e);
          }
          
          return uniqueSearches;
        });
      }
      
      if (callback) {
        callback();
      }
    }, 800);
  }, [setSearchQuery, recentSearches, elasticSearch]);

  // Cancel search and reset
  const cancelSearch = useCallback(() => {
    clearSearch();
    setElasticResults([]);
    setIsSearchActive(false);
  }, [clearSearch]);
  
  return {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    isSearchVisible,
    setIsSearchVisible,
    toggleSearchVisibility,
    clearSearch,
    cancelSearch,
    lastSearch: recentSearches,
    performSearch,
    recentSearches,
    isSearchActive,
    elasticResults
  };
};
